import {
    Container, Divider,
    Dropdown,
    DropdownItemProps,
    DropdownProps,
    Form,
    Header,
    InputOnChangeData, Label,
    Tab
} from "semantic-ui-react";
import {SurveyCampaignFormModel, SurveyCampaignModel} from "../model/SurveyCampaignModel";
import React, {ChangeEvent, useEffect, useState} from "react";
import {AppListPageDialogContext, AppListPageDialogContextState} from "../../common/AppListPage";
import {SurveyFormDataProvider} from "../service/SurveyFormDataProvider";
import {SurveyFormModel} from "../model/SurveyFormModel";
import AppDatePicker from "../../common/AppDatePicker";
import AppTimePicker from "../../common/AppTimePicker";
import {getPatientTitle, PatientTitleCase} from "../../../utils/common";
import SurveyCampaignSpecialGroups from "./SurveyCampaignSpecialGroups";
import SurveyCampaignPatients from "./SurveyCampaignPatients";
import AppDateTimePicker from "../../common/AppDateTimePicker";
import moment from "moment";
import {Utils} from "../../../helpers/Utils";

interface Props {
    context: AppListPageDialogContext<SurveyCampaignModel>
}

const SurveyCampaignDetails = (props: Props) => {

    const [name, setName] = useState<string>(props.context.model?.name ?? "");
    const formsProvider = new SurveyFormDataProvider();

    const [forms, setForms] = useState<SurveyFormModel[]>([]);
    const [form, setForm] = useState<SurveyFormModel>();
    const [startDate, setStartDate] = useState<Date | undefined>(props.context.model?.startDate);
    const [endDate, setEndDate] = useState<Date | undefined>(props.context.model?.endDate);
    const [specialGroupIds, setSpecialGroupIds] = useState<string[]>(props.context.model?.specialGroupIds ?? []);
    const [patientIds, setPatientIds] = useState<string[]>(props.context.model?.patientIds ?? []);
    const [isTest, setIsTest] = useState(props.context.model?.isTest ?? false);
    const [isForAll, setIsForAll] = useState(props.context.model?.isForAll ?? false);
    const [comment, setComment] = useState<string | undefined>(props.context.model?.comment);

    const [nameError, setNameError] = useState<string>();
    const [formError, setFormError] = useState<string>();
    const [startDateError, setStartDateError] = useState<string>();
    const [endDateError, setEndDateError] = useState<string>();
    const [participantsError, setParticipantsError] = useState<string>();

    const [loaded, setLoaded] = useState(false);

    async function loadForms(): Promise<SurveyFormModel[]> {
        const result = await formsProvider.list({page: 1, limit: 999999});
        setForms(result.items);
        return result.items;
    }

    useEffect(() => {
        loadForms().then((forms) => {
            const selectedForm = props.context.model?.form?.id ?
                forms.find((x) => x.id == props.context.model!.form!.id) : undefined;
            setForm(selectedForm);
            setLoaded(true);
        })
    }, []);

    useEffect(() => {
        if (loaded) {
            Object.assign(props.context.model, {
                name: name,
                form: form,
                startDate: startDate,
                endDate: endDate,
                patientIds: patientIds,
                specialGroupIds: specialGroupIds,
                isTest: isTest,
                isForAll: isForAll,
                comment: comment
            });
            if ((name.length > 0) && nameError) setNameError(undefined);
            if (form && formError) setFormError(undefined);
            if (startDate && startDateError) setStartDateError(undefined);
            if (endDate && endDateError) setEndDateError(undefined);
            if (((specialGroupIds.length > 0) || (patientIds.length > 0)) && participantsError) setParticipantsError(undefined);
        }
    }, [name, form, startDate, endDate, patientIds, specialGroupIds, isTest, isForAll, loaded, comment]);


    async function validate(): Promise<boolean> {
        let result = true;
        setNameError(undefined);
        setFormError(undefined);
        setStartDateError(undefined);
        setEndDateError(undefined);
        setParticipantsError(undefined);
        if ((name?.length ?? 0) == 0) {
            setNameError("Наименование должно быть заполнено");
            result = false;
        }
        if (!form) {
            setFormError("Анкета должно быть заполнено");
            result = false;
        }
        if (!startDate) {
            setStartDateError("Время начала опроса должно быть заполнено");
            result = false;
        }
        if (!endDate) {
            setEndDateError("Время окончания опроса должно быть заполнено");
            result = false;
        }

        if (endDate! <= startDate!) {
            setEndDateError("Время окончания должно быть больше времени начала");
            result = false;
        }

        if (!isForAll && (patientIds.length == 0) && (specialGroupIds.length == 0)) {
            setParticipantsError("Необходимо выбрать участников опроса");
            result = false;
        }

        return result;
    }

    props.context.validateCallback = validate;

    const readOnly = props.context.state == AppListPageDialogContextState.view;

    return <Container fluid>
        <Form>
            <Form.Dropdown label={"Анкета опроса:"}
                           selection
                           required
                           disabled={readOnly}
                           error={formError}
                           value={form?.id}
                           onChange={async (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                               const formId = data.value;
                               const form = forms?.find((x) => x.id == formId);
                               await setForm(form);
                               if (form && (name.length == 0))
                                   setName(form!.name);
                           }}
                           options={forms?.map((x) => ({
                               form: x,
                               value: x.id,
                               text: x.name
                           } as DropdownItemProps))}/>

            <Form.Input label={"Наименование опроса:"} required error={nameError} value={name}
                        readOnly={readOnly}
                        onChange={(e, data) => setName(data.value)}/>


            <Form.Group inline widths={"three"}>
                <Form.Field required>
                    <label style={{marginBottom: "8px"}}>Время начала проведения опроса:</label>
                    <AppDateTimePicker value={startDate} onChange={setStartDate} error={startDateError} readOnly={readOnly}/>
                </Form.Field>
                <Form.Field required>
                    <label style={{marginBottom: "8px"}}>Время окончания проведения опроса:</label>
                    <AppDateTimePicker value={endDate} onChange={setEndDate} error={endDateError}  readOnly={readOnly}/>
                </Form.Field>
                {!readOnly &&
                <Form.Button content={"Сейчас"} type={"button"}
                             color={"teal"}
                             onClick={() => {
                                 setStartDate(new Date());
                                 setEndDate(moment(new Date()).add(2, "hours").toDate());
                             }}></Form.Button>}
            </Form.Group>


            <Divider/>
            <Form.Field >
                <label style={{marginBottom: "8px"}}>Участники опроса:</label>

                <Form.Checkbox label={`Провести опрос по всем ${getPatientTitle(PatientTitleCase.datm)}` }
                               readOnly={readOnly}
                               checked={isForAll} onChange={(e, data) => {
                    setIsForAll(data.checked ?? false);
                }}/>

                <div hidden={isForAll}>
                <Form.Group widths={"equal"} hidden={isForAll} >
                    <Form.Field>
                        <label>Особые группы</label>
                        <SurveyCampaignSpecialGroups specialGroupIds={specialGroupIds} onChange={setSpecialGroupIds} disabled={readOnly}/>
                    </Form.Field>
                    <Form.Field>
                        <label>{getPatientTitle(PatientTitleCase.tab)}</label>
                        <SurveyCampaignPatients patientIds={patientIds} onChange={setPatientIds}  disabled={readOnly}/>
                    </Form.Field>
                </Form.Group>
                {participantsError && <Label basic color='red' pointing>{participantsError}</Label>}
                </div>
            </Form.Field>
            <Divider/>

            <Form.TextArea label={"Примечания"} value={comment}
                           readOnly={readOnly}
                           onChange={(e, v) => setComment(v.value?.toString())}/>
        </Form>
    </Container>;
}

export default SurveyCampaignDetails;